import { useCallback, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { Identifier } from 'dnd-core'
import {
  GripVertical,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Link2,
  LayoutGrid,
} from 'lucide-react'
import { Badge } from '../ui/badge'
import { StoreNavigationItem } from '../../services/navigation.service'

const DRAG_TYPE = 'NAV_ITEM'

interface DragItem {
  index: number
  id: number
  type: string
}

// ──── Etiquetas ────────────────────────────────────────────────────────────
const TARGET_TYPE_LABELS: Record<string, string> = {
  PRODUCT: 'Producto',
  CATEGORY: 'Categoría',
  DEPARTMENT: 'Departamento',
  PRODUCT_TYPE: 'Tipo de Producto',
  PROMOTIONS: 'Promociones',
  URL: 'URL',
}

// ──── Item draggable ───────────────────────────────────────────────────────
interface SortableNavItemProps {
  item: StoreNavigationItem
  index: number
  moveItem: (dragIndex: number, hoverIndex: number) => void
  onEdit: () => void
  onDelete: () => void
  onToggleStatus: () => void
}

export function SortableNavItem({
  item,
  index,
  moveItem,
  onEdit,
  onDelete,
  onToggleStatus,
}: SortableNavItemProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: DRAG_TYPE,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(dragItem, monitor) {
      if (!ref.current) return
      const dragIndex = dragItem.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      dragItem.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ id: item.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drag(drop(ref))

  const targetLabel = item.item_type === 'LINK' && item.target_type
    ? TARGET_TYPE_LABELS[item.target_type] ?? item.target_type
    : null

  const previewRef = useRef<HTMLDivElement>(null)
  preview(previewRef)

  return (
    <div
      ref={previewRef}
      data-handler-id={handlerId}
      className={`flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-all ${isDragging ? 'opacity-40 scale-95' : 'opacity-100'
        }`}
    >
      {/* Drag handle */}
      <div
        ref={ref}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-card-foreground transition-colors"
      >
        <GripVertical className="h-4 w-4" />
      </div>


      {/* Tipo de ítem */}
      <div className="flex items-center justify-center h-8 w-8 rounded-md bg-muted shrink-0">
        {item.item_type === 'CATEGORY_SELECTOR' ? (
          <LayoutGrid className="h-4 w-4 text-primary" />
        ) : (
          <Link2 className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-card-foreground truncate">{item.label}</p>
        <p className="text-xs text-muted-foreground truncate">
          {item.item_type === 'CATEGORY_SELECTOR'
            ? 'Selector de categorías'
            : targetLabel
              ? `→ ${targetLabel}${item.target_url ? `: ${item.target_url}` : ''}`
              : '—'}
        </p>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 shrink-0">
        {item.target_type === 'PROMOTIONS' && (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
            Promo
          </Badge>
        )}
        <Badge
          className={
            item.is_active
              ? 'bg-[#DCFCE6] text-[#008438]'
              : 'bg-[#FFE2E2] text-[#C10008]'
          }
        >
          {item.is_active ? 'Activo' : 'Inactivo'}
        </Badge>
      </div>

      {/* Acciones */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onToggleStatus}
          title={item.is_active ? 'Desactivar' : 'Activar'}
          className={`rounded p-1.5 transition-colors ${item.is_active
              ? 'text-primary hover:bg-primary/10'
              : 'text-muted-foreground hover:bg-muted'
            }`}
        >
          {item.is_active ? (
            <ToggleRight className="h-5 w-5" />
          ) : (
            <ToggleLeft className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={onEdit}
          title="Editar"
          className="rounded p-1.5 text-card-foreground transition-colors hover:bg-muted"
        >
          <Edit className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          title="Eliminar"
          className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ──── Panel completo de ítems (lista drag-and-drop) ─────────────────────────
interface NavigationItemsListProps {
  items: StoreNavigationItem[]
  onMoveItem: (dragIndex: number, hoverIndex: number) => void
  onEdit: (item: StoreNavigationItem) => void
  onDelete: (item: StoreNavigationItem) => void
  onToggleStatus: (item: StoreNavigationItem) => void
}

export function NavigationItemsList({
  items,
  onMoveItem,
  onEdit,
  onDelete,
  onToggleStatus,
}: NavigationItemsListProps) {
  const moveItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      onMoveItem(dragIndex, hoverIndex)
    },
    [onMoveItem],
  )

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-center">
        <Link2 className="mb-3 h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">Este menú no tiene enlaces todavía.</p>
        <p className="text-xs text-muted-foreground mt-1">Haz clic en "Agregar Enlace" para comenzar.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <SortableNavItem
          key={item.id}
          item={item}
          index={index}
          moveItem={moveItem}
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
          onToggleStatus={() => onToggleStatus(item)}
        />
      ))}
    </div>
  )
}
